import { PERKS_DISCOUNT_TITLE } from '../templates/components/perks/constants';
import { formatMoney } from '@shopify/theme-currency';
import { getIsServicesEnabled } from './getIsServicesEnabled';
import { JbCart, JbCartDiscount, JbCartItemDiscount } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { reOrderCartItems } from '@jbhi-fi/utils/src/services/reOrderCartItems';
import { OLD_APPLIANCE_REMOVAL_SKU } from '@jbhi-fi/utils/src/services/constants';

const mergeCartDiscounts = (discounts: Array<JbCartDiscount>): Array<JbCartDiscount> => {
  const mergedDiscounts: { [key: string]: JbCartDiscount } = {};

  discounts.forEach(discount => {
    const key = `${discount.code}-${discount.message}`;

    if (key in mergedDiscounts) {
      mergedDiscounts[key].amount += discount.amount;
      mergedDiscounts[key].formattedAmount = formatMoney(mergedDiscounts[key].amount * 100);
    } else {
      mergedDiscounts[key] = { ...discount, formattedAmount: formatMoney(discount.amount * 100) };
    }
  });

  return Object.values(mergedDiscounts);
};

export const applyLegacyTransformations = (cart: JbCart | undefined) => {
  if (!cart?.items) return;

  cart.items?.forEach(item => {
    // Convert cart line items and totals to currency format
    const formattedLinePrice = formatMoney(item.line_price, window.theme.moneyFormat);
    item.formattedLinePrice = formattedLinePrice;
    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
      item.formattedLinePrice = formatMoney(item.final_line_price, window.theme.moneyFormat);
    }
    const formattedOriginalLinePrice = formatMoney(item.original_line_price, window.theme.moneyFormat);
    item.formattedOriginalLinePrice = formattedOriginalLinePrice;

    item.isServicesEnabled = getIsServicesEnabled();

    // Add bundle suggestions to cart items
    const suggestionsForItem = window.Jbhifi.cartSuggestions?.find(
      suggestion => suggestion.suggestedHits && suggestion.productSkus.includes(Number(item.sku))
    );
    if (suggestionsForItem) {
      // We only want to show first available suggestion that is not already in the cart
      item.bundleSuggestions = suggestionsForItem.suggestedHits
        ?.filter(hit => cart.items.every(cartItem => cartItem.sku !== hit.sku))
        ?.filter(hit => hit.button === 'Buy' || hit.button === 'PreOrder')
        .slice(0, 1);
    }
  });

  if (cart.cartDiscounts) {
    cart.cartDiscounts = mergeCartDiscounts(cart.cartDiscounts);
  }

  cart.formattedTotalDiscount = formatMoney(cart.total_discount, window.theme.moneyFormat);
  cart.formattedOriginalTotalPrice = formatMoney(cart.original_total_price, window.theme.moneyFormat);
  cart.formattedTotalPrice = formatMoney(cart.total_price, window.theme.moneyFormat);

  cart.items = reOrderCartItems(cart.items, OLD_APPLIANCE_REMOVAL_SKU, 'end');
};

export const shouldHideOrderDiscounts = (order: {
  line_items: Array<{ applied_discounts: Array<{ description: string }> }>;
}) => {
  return order?.line_items?.some(item =>
    item.applied_discounts.some(discount => discount.description.includes(PERKS_DISCOUNT_TITLE))
  );
};

export const getCartQuantities = (cart: JbCart | undefined) => {
  if (!cart?.items) return {};
  return cart.items.reduce<{ [variantId: string]: { quantity: number; maxQty: number } }>((acc, item) => {
    const variantId = item.id;
    acc[variantId] = {
      quantity: (acc[variantId]?.quantity || 0) + item.quantity,
      maxQty: Number(item.properties?._maxQty) || 5,
    };
    return acc;
  }, {});
};
